"use client";

import { useServerInsertedHTML } from "next/navigation";
import { useEffect, useRef } from "react";

interface StructuredDataProps {
  data: object | object[];
  id?: string;
}

/**
 * Inyecta JSON-LD dentro del <head>.
 * - Carga inicial (SSR/SSG): useServerInsertedHTML emite los scripts antes de </head>.
 * - Navegación client-side: el efecto los agrega/quita de document.head,
 *   ya que useServerInsertedHTML solo corre en el servidor.
 */
export function StructuredData({ data, id }: StructuredDataProps) {
  const jsonLd = Array.isArray(data) ? data : [data];
  const serialized = jsonLd.map((item) =>
    JSON.stringify(item).replace(/</g, "\\u003c")
  );
  const getScriptId = (index: number) => {
    if (!id) return `schema-${index}`;
    return index === 0 ? id : `${id}-${index}`;
  };

  const inserted = useRef(false);
  useServerInsertedHTML(() => {
    if (inserted.current) return null;
    inserted.current = true;
    return (
      <>
        {serialized.map((json, index) => (
          <script
            id={getScriptId(index)}
            key={getScriptId(index)}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: json }}
          />
        ))}
      </>
    );
  });

  const serializedKey = serialized.join("\n");
  useEffect(() => {
    const scriptIds = serialized.map((json, index) => {
      const scriptId = getScriptId(index);
      if (!document.getElementById(scriptId)) {
        const script = document.createElement("script");
        script.id = scriptId;
        script.type = "application/ld+json";
        script.textContent = json;
        document.head.appendChild(script);
      }
      return scriptId;
    });
    return () => {
      scriptIds.forEach((scriptId) =>
        document.getElementById(scriptId)?.remove()
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, serializedKey]);

  return null;
}
